// @ts-nocheck
(() => {
  const COMPONENT_NAME = 'spx-home-banner';

  class SpxHomeBanner extends HTMLElement {
    #abortController;
    #activeVideo;
    #panels = [];
    #enteredPanels = new WeakSet();
    #playRequests = new WeakMap();
    #slider;
    #videoBreakpoint;

    connectedCallback() {
      this.#abortController?.abort();
      this.#abortController = new AbortController();
      this.#panels = [];

      this.#slider = this.querySelector('spx-slider');
      const copyStack = this.querySelector('[data-spx-banner-copy-stack]');
      const templates = Array.from(
        this.querySelectorAll('template[data-spx-banner-copy-template]')
      );

      if (copyStack instanceof HTMLElement) {
        const copies = templates
          .filter((template) => template instanceof HTMLTemplateElement)
          .map((template) => template.content.cloneNode(true));
        copyStack.replaceChildren(...copies);
        this.#panels = Array.from(copyStack.querySelectorAll('[data-spx-banner-panel]'));
      }

      this.#slider?.addEventListener('spx:slide-change', this.#handleSlideChange, {
        signal: this.#abortController.signal,
      });
      this.#slider?.addEventListener('spx:slider-ready', this.#handleSliderReady, {
        signal: this.#abortController.signal,
      });
      this.addEventListener('click', this.#handleVideoControlClick, {
        signal: this.#abortController.signal,
      });
      this.#videoBreakpoint = window.matchMedia('(min-width: 750px)');
      this.#videoBreakpoint.addEventListener('change', this.#handleVideoBreakpointChange, {
        signal: this.#abortController.signal,
      });
      document.addEventListener('visibilitychange', this.#handleVisibilityChange, {
        signal: this.#abortController.signal,
      });

      this.querySelectorAll('video[data-spx-banner-video]').forEach((video) => {
        video.addEventListener('error', this.#handleVideoUnavailable, {
          signal: this.#abortController.signal,
        });
        video.addEventListener('emptied', this.#handleVideoUnavailable, {
          signal: this.#abortController.signal,
        });
      });

      this.#activatePanel(0);
      this.#syncAllVideoControls();
      requestAnimationFrame(() => this.#syncMediaFromSlider());
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#pauseAllVideos();
      this.#slider = undefined;
      this.#videoBreakpoint = undefined;
    }

    #handleSlideChange = (event) => {
      const realIndex = Number(event.detail?.realIndex ?? event.detail?.activeIndex ?? 0);
      const activeIndex = Number(event.detail?.activeIndex ?? 0);
      this.#activatePanel(realIndex);
      this.#activateMedia(activeIndex);
    };

    #handleSliderReady = (event) => {
      const realIndex = Number(event.detail?.realIndex ?? event.detail?.activeIndex ?? 0);
      const activeIndex = Number(event.detail?.activeIndex ?? 0);
      this.#activatePanel(realIndex);
      this.#activateMedia(activeIndex);
    };

    #handleVideoControlClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const control = event.target.closest('[data-spx-video-control]');
      if (!(control instanceof HTMLButtonElement)) return;

      const video = control
        .closest('.spx-home-banner-slide__media')
        ?.querySelector('video[data-spx-banner-video]');
      if (!(video instanceof HTMLVideoElement) || video !== this.#activeVideo) return;

      if (video.paused) {
        video.dataset.userActivated = 'true';
        delete video.dataset.userPaused;
        this.#playVideo(video);
      } else {
        video.dataset.userPaused = 'true';
        video.pause();
        this.#setVideoControlState(video, false);
      }
    };

    #handleVideoBreakpointChange = () => {
      this.#pauseAllVideos();
      this.querySelectorAll('video[data-spx-banner-video]').forEach((video) => video.load());
      this.#syncMediaFromSlider();
    };

    #handleVisibilityChange = () => {
      if (document.hidden) {
        this.#pauseAllVideos();
      } else {
        this.#syncMediaFromSlider();
      }
    };

    #handleVideoUnavailable = (event) => {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement)) return;

      video.classList.remove('is-playing');
      this.#setVideoControlState(video, false);
    };

    #activatePanel(index) {
      this.#panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;
        if (isActive && !panel.classList.contains('is-active')) this.#preparePanelEntrance(panel);
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', String(!isActive));
        panel.inert = !isActive;
      });
    }

    #preparePanelEntrance(panel) {
      const reveal = this.closest('spx-reveal');
      if (!reveal || reveal.dataset.entranceAnimation === 'none' ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
          !('IntersectionObserver' in window) || !Element.prototype.animate) return;
      if (reveal.dataset.entranceOnce !== 'false' && this.#enteredPanels.has(panel)) return;

      const textEffect = ['reveal', 'blur'].includes(reveal.dataset.titleAnimation);
      const selector = reveal.dataset.entranceAnimation === 'fade'
        ? `[data-reveal-whole]${textEffect ? ', [data-reveal-text]' : ''}`
        : '[data-reveal-item]';
      const targets = [...panel.querySelectorAll(selector)];
      // The panel is still hidden here. Prepare the copy before its first paint,
      // even when the reveal module loads after this classic deferred script.
      targets.forEach((target) => target.setAttribute('data-spx-reveal-initial', ''));
      this.#enteredPanels.add(panel);
      // The reveal controller replaces these markers with owned pending states.
      // If it fails to load or fonts stall, release only the unclaimed markers.
      window.setTimeout(() => {
        targets.forEach((target) => target.removeAttribute('data-spx-reveal-initial'));
      }, 2000);
    }

    #syncMediaFromSlider() {
      const swiper = this.#slider?.swiperInstance;
      this.#activateMedia(Number(swiper?.activeIndex ?? 0));
    }

    #activateMedia(activeIndex) {
      const videos = Array.from(this.querySelectorAll('video[data-spx-banner-video]'));
      const swiperSlides = this.#slider?.swiperInstance?.slides;
      const fallbackSlides = this.#slider?.querySelectorAll('.spx-home-banner-slide');
      const slides = swiperSlides?.length ? swiperSlides : fallbackSlides;
      const activeSlide = slides?.[activeIndex];
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.#activeVideo = activeSlide?.querySelector('video[data-spx-banner-video]');

      videos.forEach((video) => {
        const autoplay = video.dataset.spxVideoAutoplay === 'true';
        const userActivated = video.dataset.userActivated === 'true';
        const userPaused = video.dataset.userPaused === 'true';
        const shouldPlay =
          video === this.#activeVideo &&
          !document.hidden &&
          !userPaused &&
          (userActivated || (autoplay && !reduceMotion));

        if (shouldPlay) {
          this.#playVideo(video);
        } else {
          video.pause();
          video.classList.remove('is-playing');
          this.#setVideoControlState(video, false);
        }
      });
    }

    #playVideo(video) {
      const requestId = (this.#playRequests.get(video) || 0) + 1;
      this.#playRequests.set(video, requestId);
      video.muted = true;
      const playPromise = video.play();

      if (playPromise instanceof Promise) {
        playPromise
          .then(() => {
            if (this.#playRequests.get(video) !== requestId) return;
            if (
              video === this.#activeVideo &&
              !video.paused &&
              video.dataset.userPaused !== 'true' &&
              !document.hidden
            ) {
              video.classList.add('is-playing');
              this.#setVideoControlState(video, true);
            } else if (!video.paused) {
              video.pause();
              video.classList.remove('is-playing');
              this.#setVideoControlState(video, false);
            }
          })
          .catch(() => {
            if (this.#playRequests.get(video) !== requestId) return;
            video.classList.remove('is-playing');
            this.#setVideoControlState(video, false);
          });
      } else {
        video.classList.add('is-playing');
        this.#setVideoControlState(video, true);
      }
    }

    #setVideoControlState(video, isPlaying) {
      const control = video
        .closest('.spx-home-banner-slide__media')
        ?.querySelector('[data-spx-video-control]');
      if (!(control instanceof HTMLButtonElement)) return;

      control.dataset.state = isPlaying ? 'playing' : 'paused';
      control.setAttribute('aria-label', isPlaying ? '暂停视频' : '播放视频');
    }

    #syncAllVideoControls() {
      this.querySelectorAll('video[data-spx-banner-video]').forEach((video) => {
        this.#setVideoControlState(video, !video.paused);
      });
    }

    #pauseAllVideos() {
      this.#activeVideo = undefined;
      this.querySelectorAll('video[data-spx-banner-video]').forEach((video) => {
        video.pause();
        video.classList.remove('is-playing');
        this.#setVideoControlState(video, false);
      });
    }
  }

  if (!customElements.get(COMPONENT_NAME)) {
    customElements.define(COMPONENT_NAME, SpxHomeBanner);
  }
})();
